import PropTypes from "prop-types";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { POPUP_STATUS, QUERY_STATUS } from "constants/constants";
import {
  DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES,
  DELIVERY_NOTE_SHARE_ACTIONS,
  DELIVERY_NOTE_TEMPLATE_ACTIONS,
  DELIVERY_NOTE_TEMPLATE_DELETE,
  DELIVERY_NOTE_TEMPLATE_MESSAGES,
} from "constants/forms";
import {
  DELIVERY_NOTE_TEMPLATE_FAVORITE_ERROR,
  DELIVERY_NOTE_TEMPLATE_PUBLIC_ERROR,
} from "constants/popup-codes";
import InfoContext from "pages/InfoContext";
import DeleteModal from "components/modal/DeleteModal";
import Icon from "core/icon/Icon";
import LoaderV2 from "core/loader/LoaderV2";
import { Typography } from "core/typography/Typography";
import DeliveryNoteTemplatePreviewModal from "./DeliveryNoteTemplatePreviewModal";
import {
  countColumnsByType,
  sortTemplatesByFavorite,
} from "utils/deliveryNoteHelper";

const FAVORITE_ORDER_RELEASE_DELAY = 600;
const MOUSE_POINTER_TYPE = "mouse";
const KEYBOARD_CLICK_DETAIL = 0;
const ITEM_CLASS = "delivery-note-templates__item";

const DeliveryNoteTemplateList = ({
  templates,
  hasActiveFilters,
  filters,
  loadStatus,
  onEdit,
  onToggleFavorite,
  onTogglePublic,
  onShare,
  onDelete,
  isDeleting,
}) => {
  const intl = useIntl();
  const { setCode, setStatus, confirmAction } = useContext(InfoContext);
  const editLabel = intl.formatMessage(DELIVERY_NOTE_TEMPLATE_ACTIONS.edit);
  const shareLabel = intl.formatMessage(DELIVERY_NOTE_SHARE_ACTIONS.share);
  const addFavoriteLabel = intl.formatMessage(
    DELIVERY_NOTE_TEMPLATE_ACTIONS.addFavorite
  );
  const removeFavoriteLabel = intl.formatMessage(
    DELIVERY_NOTE_TEMPLATE_ACTIONS.removeFavorite
  );
  const deleteLabel = intl.formatMessage(DELIVERY_NOTE_TEMPLATE_ACTIONS.delete);
  const previewLabel = intl.formatMessage(
    DELIVERY_NOTE_TEMPLATE_ACTIONS.preview
  );
  const makePublicLabel = intl.formatMessage(
    DELIVERY_NOTE_TEMPLATE_ACTIONS.makePublic
  );
  const removePublicLabel = intl.formatMessage(
    DELIVERY_NOTE_TEMPLATE_ACTIONS.removePublic
  );

  const [deletedTemplate, setDeletedTemplate] = useState(null);
  const [previewedTemplate, setPreviewedTemplate] = useState(null);

  const [heldOrder, setHeldOrder] = useState(null);

  const raiseToggleError = (code) => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(code);
  };

  const handleToggleFavorite = async (template) => {
    if ((await onToggleFavorite(template)) === false) {
      raiseToggleError(DELIVERY_NOTE_TEMPLATE_FAVORITE_ERROR);
    }
  };

  const handleTogglePublic = async (template) => {
    if ((await onTogglePublic(template)) === false) {
      raiseToggleError(DELIVERY_NOTE_TEMPLATE_PUBLIC_ERROR);
    }
  };

  const confirmDelete = async () => {
    const template = deletedTemplate;
    setDeletedTemplate(null);
    confirmAction({ status: QUERY_STATUS.LOADING });
    const deleted = await onDelete({ id: template.id });
    confirmAction({
      status: deleted ? QUERY_STATUS.SUCCESS : QUERY_STATUS.ERROR,
      messages: DELIVERY_NOTE_CONFIRM_MODAL_MESSAGES.templateDelete,
    });
  };

  const sortedTemplates = useMemo(
    () => sortTemplatesByFavorite(templates),
    [templates]
  );

  const displayedTemplates = useMemo(() => {
    if (!heldOrder) return sortedTemplates;
    const rank = new Map(heldOrder.map((id, index) => [id, index]));
    const rankOf = (item) => rank.get(item.id) ?? heldOrder.length;
    return [...sortedTemplates].sort((a, b) => rankOf(a) - rankOf(b));
  }, [sortedTemplates, heldOrder]);

  const releaseTimerRef = useRef(null);
  const lastPointerTypeRef = useRef(null);
  const releasesOnBlurRef = useRef(false);

  const releaseOrder = useCallback(() => {
    clearTimeout(releaseTimerRef.current);
    setHeldOrder(null);
  }, []);

  useEffect(() => () => clearTimeout(releaseTimerRef.current), []);

  useEffect(() => {
    releaseOrder();
  }, [filters, releaseOrder]);

  const rememberPointerType = (event) => {
    lastPointerTypeRef.current = event.pointerType;
  };

  const consumePointerType = () => {
    const pointerType = lastPointerTypeRef.current;
    lastPointerTypeRef.current = null;
    return pointerType;
  };

  const toggleFavorite = (template, { detail }) => {
    const pointerType = consumePointerType();
    const fromKeyboard = detail === KEYBOARD_CLICK_DETAIL;

    setHeldOrder(
      (current) => current || displayedTemplates.map((item) => item.id)
    );
    releasesOnBlurRef.current = fromKeyboard;
    clearTimeout(releaseTimerRef.current);
    if (!fromKeyboard && pointerType !== MOUSE_POINTER_TYPE) {
      releaseTimerRef.current = setTimeout(
        releaseOrder,
        FAVORITE_ORDER_RELEASE_DELAY
      );
    }
    handleToggleFavorite(template);
  };

  const releaseOrderOnPointerLeave = (event) => {
    if (event.pointerType === MOUSE_POINTER_TYPE) releaseOrder();
  };

  const releaseOrderOnFocusLoss = (event) => {
    if (!releasesOnBlurRef.current) return;
    if (!event.currentTarget.contains(event.relatedTarget)) releaseOrder();
  };

  if (loadStatus === QUERY_STATUS.LOADING) {
    return <LoaderV2 id="delivery-note-templates-loader" />;
  }

  return (
    <section className="delivery-note-templates">
      {loadStatus === QUERY_STATUS.ERROR && (
        <Typography
          className="text-3 alizarin-crimson"
          type="p"
          label={
            <FormattedMessage
              id="delivery_note_templates_load_error"
              defaultMessage="Les modèles n'ont pas pu être chargés."
            />
          }
        />
      )}

      {loadStatus === QUERY_STATUS.SUCCESS && !templates?.length && (
        <Typography
          className="text-3 violet"
          type="p"
          label={
            <FormattedMessage
              {...(hasActiveFilters
                ? DELIVERY_NOTE_TEMPLATE_MESSAGES.noMatch
                : DELIVERY_NOTE_TEMPLATE_MESSAGES.empty)}
            />
          }
        />
      )}

      <ul
        className="delivery-note-templates__list"
        onPointerLeave={releaseOrderOnPointerLeave}
        onBlur={releaseOrderOnFocusLoss}
      >
        {displayedTemplates.map((template) => {
          const counts = countColumnsByType(template.columns);
          const favoriteLabel = template.is_favorite
            ? removeFavoriteLabel
            : addFavoriteLabel;
          const publicLabel = template.is_public
            ? removePublicLabel
            : makePublicLabel;
          const itemClass = template.is_favorite
            ? `${ITEM_CLASS} ${ITEM_CLASS}--favorite`
            : ITEM_CLASS;
          return (
            <li className={itemClass} key={template.id}>
              <div className="delivery-note-templates__identity">
                <div className="delivery-note-templates__name">
                  <Typography
                    className="title-5 deep-purple"
                    type="span"
                    label={template.name}
                  />
                  {template.is_public && (
                    <span
                      className="delivery-note-templates__badge"
                      data-testid={`delivery-note-template-public-badge-${template.id}`}
                    >
                      <FormattedMessage
                        {...DELIVERY_NOTE_TEMPLATE_MESSAGES.publicBadge}
                      />
                    </span>
                  )}
                </div>
                <Typography
                  className="text-3 violet"
                  type="span"
                  label={
                    <FormattedMessage
                      id="delivery_note_templates_item_detail"
                      defaultMessage="{issuer} → {recipient} · {simple} simple(s), {linked} liée(s)"
                      values={{
                        issuer: template.issuer?.company_name || "—",
                        recipient: template.recipient?.company_name || "—",
                        simple: counts.simple,
                        linked: counts.linked,
                      }}
                    />
                  }
                />
              </div>
              <div className="delivery-note-templates__buttons">
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onClick={() => setPreviewedTemplate(template)}
                  aria-label={previewLabel}
                  title={previewLabel}
                  data-testid={`delivery-note-template-preview-${template.id}`}
                >
                  <Icon icon="eye" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onClick={() => onShare(template)}
                  aria-label={shareLabel}
                  title={shareLabel}
                  data-testid={`delivery-note-template-share-${template.id}`}
                >
                  <Icon icon="share" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onClick={() => onEdit(template)}
                  aria-label={editLabel}
                  title={editLabel}
                  data-testid={`delivery-note-template-edit-${template.id}`}
                >
                  <Icon icon="pencil" color="violet" />
                </button>
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onPointerDown={rememberPointerType}
                  onClick={(event) => toggleFavorite(template, event)}
                  aria-label={favoriteLabel}
                  title={favoriteLabel}
                  data-testid={`delivery-note-template-favorite-${template.id}`}
                >
                  <Icon
                    icon={template.is_favorite ? "star2" : "white-star"}
                    color={template.is_favorite ? "gamboge" : "violet"}
                  />
                </button>
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onClick={() => handleTogglePublic(template)}
                  aria-label={publicLabel}
                  title={publicLabel}
                  data-testid={`delivery-note-template-public-${template.id}`}
                >
                  <Icon
                    icon={template.is_public ? "lock-open" : "lock"}
                    color={template.is_public ? "green" : "violet"}
                  />
                </button>
                <button
                  type="button"
                  className="delivery-note-templates__action"
                  onClick={() => setDeletedTemplate(template)}
                  disabled={isDeleting}
                  aria-label={deleteLabel}
                  title={deleteLabel}
                  data-testid={`delivery-note-template-delete-${template.id}`}
                >
                  <Icon icon="trash-2" color="red" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {previewedTemplate && (
        <DeliveryNoteTemplatePreviewModal
          template={previewedTemplate}
          onClose={() => setPreviewedTemplate(null)}
        />
      )}

      {deletedTemplate && (
        <DeleteModal
          open
          onClose={() => setDeletedTemplate(null)}
          onValidate={confirmDelete}
          validationMessages={DELIVERY_NOTE_TEMPLATE_DELETE.validationMessages}
          highlight={deletedTemplate.name}
        />
      )}
    </section>
  );
};

DeliveryNoteTemplateList.propTypes = {
  templates: PropTypes.array,
  hasActiveFilters: PropTypes.bool,
  filters: PropTypes.object,
  loadStatus: PropTypes.string,
  onEdit: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onTogglePublic: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
};

export default DeliveryNoteTemplateList;
